"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getActiveDictationTargetId } from "@/lib/dictation/target-registry";
import { useIsMobile } from "@/hooks/use-mobile";
import { WritingToolOrb } from "./writing-tool-orb";
type WritingTool = "dictation" | "brainstorming" | null;
export interface ChapterNotesTool {
  activate: () => void | Promise<void>;
  disabled: boolean;
  enabled: boolean;
  loading?: boolean;
  sideBySide?: boolean;
  toggleSideBySide?: () => void;
}
interface WritingToolsState {
  activeTool: WritingTool;
  targetId: string | null;
  assistantOpen: boolean;
  setAssistantOpen: (open: boolean) => void;
  setActiveTool: (tool: WritingTool, targetId?: string | null) => void;
  chapterNotes: ChapterNotesTool | null;
  setChapterNotes: (tool: ChapterNotesTool | null) => void;
}
const WritingToolsContext = createContext<WritingToolsState | null>(null);

/** Scoped to the current chapter, shared by desktop and mobile presentation. */
export function WritingToolsProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== children || $[1] !== pathname) {
    t1 = <WritingToolsStateProvider key={pathname}>{children}</WritingToolsStateProvider>;
    $[0] = children;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function WritingToolsStateProvider(t0) {
  const $ = _c(13);
  const {
    children
  } = t0;
  const isMobile = useIsMobile();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      activeTool: null,
      targetId: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selection, selectTool] = useState(t1);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [chapterNotes, setChapterNotes] = useState(null);
  let t2;
  if ($[1] !== assistantOpen || $[2] !== chapterNotes || $[3] !== selection) {
    t2 = {
      ...selection,
      assistantOpen,
      setAssistantOpen,
      chapterNotes,
      setChapterNotes,
      setActiveTool(tool, t3) {
        const targetId = t3 === undefined ? getActiveDictationTargetId() : t3;
        selectTool({
          activeTool: tool,
          targetId: tool ? targetId : null
        });
      }
    };
    $[1] = assistantOpen;
    $[2] = chapterNotes;
    $[3] = selection;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const value = t2;
  let t3;
  if ($[5] !== assistantOpen || $[6] !== isMobile || $[7] !== selection.activeTool) {
    t3 = selection.activeTool && !(isMobile && assistantOpen) && <WritingToolOrb />;
    $[5] = assistantOpen;
    $[6] = isMobile;
    $[7] = selection.activeTool;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== t3 || $[11] !== value) {
    t4 = <WritingToolsContext.Provider value={value}>{children}{t3}</WritingToolsContext.Provider>;
    $[9] = children;
    $[10] = t3;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export function useWritingTools() {
  return useContext(WritingToolsContext);
}

/** Register chapter-owned activation for both toolbar and sibling mobile menus. */
export function useChapterNotesTool(tool) {
  const $ = _c(9);
  const setChapterNotes = useWritingTools()?.setChapterNotes;
  const activate = tool?.activate;
  const disabled = tool?.disabled ?? false;
  const enabled = tool?.enabled ?? false;
  const loading = tool?.loading ?? false;
  const sideBySide = tool?.sideBySide ?? false;
  const toggleSideBySide = tool?.toggleSideBySide;
  let t0;
  let t1;
  if ($[0] !== activate || $[1] !== disabled || $[2] !== enabled || $[3] !== loading || $[4] !== setChapterNotes || $[5] !== sideBySide || $[6] !== toggleSideBySide) {
    t0 = () => {
      if (!activate) {
        return;
      }
      setChapterNotes?.({
        activate,
        disabled,
        enabled,
        loading,
        sideBySide,
        toggleSideBySide
      });
      return () => setChapterNotes?.(null);
    };
    t1 = [activate, disabled, enabled, loading, sideBySide, toggleSideBySide, setChapterNotes];
    $[0] = activate;
    $[1] = disabled;
    $[2] = enabled;
    $[3] = loading;
    $[4] = setChapterNotes;
    $[5] = sideBySide;
    $[6] = toggleSideBySide;
    $[7] = t0;
    $[8] = t1;
  } else {
    t0 = $[7];
    t1 = $[8];
  }
  useEffect(t0, t1);
}